import type React from "react";
import { createContext, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "../lib/debounce";
import {
  createKdfParams,
  decryptNotesWithKey,
  decryptNotesWithPassphrase,
  deriveKey,
  type EncryptedNotesEnvelope,
  type EnvelopeKdfParams,
  encryptNotes,
  isEncryptedEnvelope,
} from "../lib/privateNotesCrypto";

/**
 * 暗号化の 3 状態
 * - plaintext: 従来どおりの平文保存（既定）
 * - locked: v2 封筒が保存されているがパスフレーズ未入力（リロード直後もここに戻る）
 * - unlocked: パスフレーズで解除済み。導出鍵はメモリのみに保持
 */
export type PrivateNotesEncryptionStatus = "plaintext" | "locked" | "unlocked";

interface PrivateNotesContextValue {
  notes: Record<string, string>;
  saveNote: (serviceId: string, content: string) => void;
  deleteNote: (serviceId: string) => void;
  // エクスポート/インポート機能（追加）
  bulkUpdateNotes: (newNotes: Record<string, string>) => void;
  // 暗号化（パスフレーズ由来鍵・PBKDF2 + AES-GCM）
  encryptionStatus: PrivateNotesEncryptionStatus;
  /**
   * 暗号化バックアップのエクスポート用（plaintext 時は null）。
   * getter なのは、保存のたびに更新される封筒を state に載せると
   * 全コンシューマの再レンダー波が毎保存で起きるため（読むのはエクスポート実行時だけ）
   */
  getEncryptedEnvelope: () => EncryptedNotesEnvelope | null;
  /** 平文メモを暗号化して同キーに上書きする（平文は残さない） */
  enableEncryption: (passphrase: string) => Promise<void>;
  /** ロック解除。誤パスフレーズは PrivateNotesDecryptionError を throw */
  unlock: (passphrase: string) => Promise<void>;
  /** 解除済み状態から平文（従来形式）に書き戻す */
  disableEncryption: () => void;
  /** パスフレーズを忘れた場合の唯一の出口。全メモ削除して平文モードに戻す */
  resetEncryptedNotes: () => void;
  /** 初回メモ保存後にワンタイムで暗号化を勧めるダイアログの表示状態 */
  isEncryptionNudgeOpen: boolean;
  /** ナッジを閉じ、以後表示しないフラグを立てる */
  dismissEncryptionNudge: () => void;
}

export const PrivateNotesContext = createContext<PrivateNotesContextValue | undefined>(undefined);

const STORAGE_KEY = "ai-deck-private-notes";
const MAX_NOTE_LENGTH = 100;
const DEBOUNCE_DELAY = 500; // 500ms
// 初回メモ保存後の暗号化ナッジ（ワンタイム）。入力を邪魔しないよう最終編集から遅延して出す
const ENCRYPTION_NUDGE_KEY = "ai-deck-encryption-nudge-shown";
const ENCRYPTION_NUDGE_DELAY = 2000;

/**
 * LocalStorage の安全なラッパー
 */
const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error("LocalStorage getItem error:", error);
      return null;
    }
  },
  setItem: (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.error("LocalStorage setItem error:", error);
      return false;
    }
  },
};

interface InitialState {
  notes: Record<string, string>;
  status: PrivateNotesEncryptionStatus;
  envelope: EncryptedNotesEnvelope | null;
}

/** localStorage の値を判別して初期状態を決める（v2 封筒 → ロック中、生 Record → 平文） */
function readInitialState(): InitialState {
  const empty: InitialState = { notes: {}, status: "plaintext", envelope: null };
  const stored = safeLocalStorage.getItem(STORAGE_KEY);
  if (!stored) return empty;
  try {
    const parsed: unknown = JSON.parse(stored);
    if (isEncryptedEnvelope(parsed)) {
      return { notes: {}, status: "locked", envelope: parsed };
    }
    if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
      return { ...empty, notes: parsed as Record<string, string> };
    }
  } catch {
    // JSONパースエラーは初期値扱い
  }
  return empty;
}

export const PrivateNotesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [initialState] = useState(readInitialState);
  const [notes, setNotes] = useState<Record<string, string>>(initialState.notes);
  const [encryptionStatus, setEncryptionStatus] = useState<PrivateNotesEncryptionStatus>(
    initialState.status,
  );

  // 最新の notes を副作用から参照するための ref
  const notesRef = useRef(notes);
  // 現在の封筒（locked / unlocked 中のみ）。保存のたびに差し替わるため state でなく ref に持つ
  // （state に載せるとデバウンス保存の完了ごとに全コンシューマの第 2 再レンダー波が起きる）
  const envelopeRef = useRef<EncryptedNotesEnvelope | null>(initialState.envelope);
  // 導出鍵と KDF パラメータ（unlocked 中のみ・メモリのみ。localStorage には書かない）
  const cryptoRef = useRef<{ key: CryptoKey; kdf: EnvelopeKdfParams } | null>(null);
  // 暗号化保存は非同期のため、古い書き込みが新しい書き込みを上書きしないよう連番で守る
  const writeSeqRef = useRef(0);
  // callback の依存を増やさずに現在の状態を参照するための ref
  const encryptionStatusRef = useRef<PrivateNotesEncryptionStatus>(initialState.status);
  encryptionStatusRef.current = encryptionStatus;

  // 暗号化ナッジ（初回メモ保存後にワンタイム表示）
  const [isEncryptionNudgeOpen, setIsEncryptionNudgeOpen] = useState(false);
  const nudgeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 表示済みフラグは一方向（未表示 → 済）にしか変わらないため ref にキャッシュし、
  // キーストロークごとの localStorage 同期読み取りを避ける
  const isNudgeShownRef = useRef(Boolean(safeLocalStorage.getItem(ENCRYPTION_NUDGE_KEY)));

  const dismissEncryptionNudge = useCallback(() => {
    isNudgeShownRef.current = true;
    safeLocalStorage.setItem(ENCRYPTION_NUDGE_KEY, "1");
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    setIsEncryptionNudgeOpen(false);
  }, []);

  const scheduleEncryptionNudge = useCallback(() => {
    if (encryptionStatusRef.current !== "plaintext" || isNudgeShownRef.current) return;
    if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    nudgeTimerRef.current = setTimeout(() => {
      // 発火時にも再判定（表示までの間に暗号化を有効化した場合は出さない）
      if (encryptionStatusRef.current === "plaintext" && !isNudgeShownRef.current) {
        setIsEncryptionNudgeOpen(true);
      }
    }, ENCRYPTION_NUDGE_DELAY);
  }, []);

  useEffect(
    () => () => {
      if (nudgeTimerRef.current) clearTimeout(nudgeTimerRef.current);
    },
    [],
  );

  /** 現在のモードに従って保存する（unlocked: 再暗号化して封筒、plaintext: 生 JSON） */
  const persistNotes = useCallback(async (updated: Record<string, string>) => {
    const crypto = cryptoRef.current;
    if (crypto) {
      const seq = ++writeSeqRef.current;
      try {
        const envelope = await encryptNotes(updated, crypto.key, crypto.kdf);
        if (seq !== writeSeqRef.current) return; // 後続の書き込みが確定済みなら破棄
        safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
        envelopeRef.current = envelope; // status は unlocked のまま（再レンダー不要）
      } catch (error) {
        console.error("Private notes encrypt error:", error);
      }
    } else {
      safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    }
  }, []);

  // デバウンス付きLocalStorage保存関数
  // 引数で渡さず実行時に notesRef.current を読む: setNotes の updater は遅延実行されうるため、
  // 呼び出し時点のスナップショットを渡すと古い内容を保存する競合がある
  const debouncedSave = useMemo(
    () =>
      debounce(() => {
        void persistNotes(notesRef.current);
      }, DEBOUNCE_DELAY),
    [persistNotes],
  );

  // 他タブとの同期（storage イベント）
  useEffect(() => {
    // 封筒はあるが手元に鍵がない（or 復号不能）→ ロック中に落とす
    const fallToLocked = (envelope: EncryptedNotesEnvelope) => {
      cryptoRef.current = null;
      envelopeRef.current = envelope;
      notesRef.current = {};
      setNotes({});
      setEncryptionStatus("locked");
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY || !e.newValue) return;
      try {
        const parsed: unknown = JSON.parse(e.newValue);
        if (isEncryptedEnvelope(parsed)) {
          const crypto = cryptoRef.current;
          if (crypto) {
            // 解除済みタブ: 手元の鍵で再復号（他タブの編集を反映）
            decryptNotesWithKey(parsed, crypto.key)
              .then((decrypted) => {
                envelopeRef.current = parsed;
                notesRef.current = decrypted;
                setNotes(decrypted);
                setEncryptionStatus("unlocked");
              })
              // salt 変更（別パスフレーズで再有効化）等で復号不能
              .catch(() => fallToLocked(parsed));
          } else {
            // ロック中/平文タブ: 封筒だけ差し替えてロック中のまま（or ロックへ遷移）
            fallToLocked(parsed);
          }
        } else if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)) {
          // 平文 Record（他タブが無効化・リセット・平文更新）
          cryptoRef.current = null;
          envelopeRef.current = null;
          const plainNotes = parsed as Record<string, string>;
          notesRef.current = plainNotes;
          setNotes(plainNotes);
          setEncryptionStatus("plaintext");
        }
      } catch {
        // JSONパースエラーは無視
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const saveNote = useCallback(
    (serviceId: string, content: string) => {
      // ロック中は編集 UI を出さないため通常到達しない（防御的ガード）
      if (encryptionStatusRef.current === "locked") return;

      // notesRef を正として先に更新し、state はそれを反映する（updater の遅延実行に依存しない）
      const updated = { ...notesRef.current };
      const hasContent = content.trim().length > 0;

      // trim()は空文字チェックのみに使用し、実際の値は保存する
      if (hasContent) {
        // 最大文字数制限（trimせずに保存）
        updated[serviceId] = content.slice(0, MAX_NOTE_LENGTH);
      } else {
        // 空文字の場合は削除
        delete updated[serviceId];
      }

      notesRef.current = updated;
      setNotes(updated);
      debouncedSave();

      // 平文モードでメモが書かれたら、ワンタイムの暗号化ナッジを予約
      if (hasContent) {
        scheduleEncryptionNudge();
      }
    },
    [debouncedSave, scheduleEncryptionNudge],
  );

  const deleteNote = useCallback(
    (serviceId: string) => {
      if (encryptionStatusRef.current === "locked") return;

      const updated = { ...notesRef.current };
      delete updated[serviceId];

      notesRef.current = updated;
      setNotes(updated);
      void persistNotes(updated);
    },
    [persistNotes],
  );

  // 一括更新メソッド（インポート用）
  const bulkUpdateNotes = useCallback(
    (newNotes: Record<string, string>) => {
      if (encryptionStatusRef.current === "locked") return;

      notesRef.current = newNotes;
      setNotes(newNotes);
      void persistNotes(newNotes);
    },
    [persistNotes],
  );

  const getEncryptedEnvelope = useCallback(() => envelopeRef.current, []);

  /** 有効化: 既存の平文メモを暗号化して同キーに上書き（平文は残さない） */
  const enableEncryption = useCallback(
    async (passphrase: string) => {
      if (encryptionStatusRef.current !== "plaintext") return;

      const kdf = createKdfParams();
      const key = await deriveKey(passphrase, kdf);
      const envelope = await encryptNotes(notesRef.current, key, kdf);
      safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));
      cryptoRef.current = { key, kdf };
      envelopeRef.current = envelope;
      setEncryptionStatus("unlocked");

      // 自力で有効化した人にナッジは不要（後で無効化しても再表示しない）
      dismissEncryptionNudge();
    },
    [dismissEncryptionNudge],
  );

  /** 解除: 復号成功で unlocked へ。誤パスフレーズは PrivateNotesDecryptionError が伝播する */
  const unlock = useCallback(async (passphrase: string) => {
    const envelope = envelopeRef.current;
    if (encryptionStatusRef.current !== "locked" || !envelope) return;

    const { notes: decrypted, key } = await decryptNotesWithPassphrase(envelope, passphrase);
    cryptoRef.current = { key, kdf: envelope.kdf };
    notesRef.current = decrypted;
    setNotes(decrypted);
    setEncryptionStatus("unlocked");
  }, []);

  /** 無効化: 解除済み状態からのみ。平文（従来形式）に書き戻す */
  const disableEncryption = useCallback(() => {
    if (encryptionStatusRef.current !== "unlocked") return;

    cryptoRef.current = null;
    envelopeRef.current = null;
    writeSeqRef.current++; // 進行中の暗号化保存を破棄
    safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(notesRef.current));
    setEncryptionStatus("plaintext");
  }, []);

  /** リセット: パスフレーズを忘れた場合の唯一の出口。全メモ削除して平文モードへ */
  const resetEncryptedNotes = useCallback(() => {
    cryptoRef.current = null;
    envelopeRef.current = null;
    writeSeqRef.current++; // 進行中の暗号化保存を破棄
    notesRef.current = {};
    setNotes({});
    safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify({}));
    setEncryptionStatus("plaintext");
  }, []);

  const value = useMemo(
    () => ({
      notes,
      saveNote,
      deleteNote,
      bulkUpdateNotes,
      encryptionStatus,
      getEncryptedEnvelope,
      enableEncryption,
      unlock,
      disableEncryption,
      resetEncryptedNotes,
      isEncryptionNudgeOpen,
      dismissEncryptionNudge,
    }),
    [
      notes,
      saveNote,
      deleteNote,
      bulkUpdateNotes,
      encryptionStatus,
      getEncryptedEnvelope,
      enableEncryption,
      unlock,
      disableEncryption,
      resetEncryptedNotes,
      isEncryptionNudgeOpen,
      dismissEncryptionNudge,
    ],
  );

  return <PrivateNotesContext.Provider value={value}>{children}</PrivateNotesContext.Provider>;
};
